import { ImgHTMLAttributes, ReactNode, useState } from 'react';
import './Avatar.css';

export interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  /**
   * Size of the avatar
   * @default 'base'
   */
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';

  /**
   * Shape of the avatar
   * @default 'circle'
   */
  shape?: 'circle' | 'square';

  /**
   * Image URL
   */
  src?: string;

  /**
   * Alt text for image
   */
  alt?: string;

  /**
   * Fallback text (usually initials) when image fails to load
   */
  fallback?: string;

  /**
   * Icon to display instead of image
   */
  icon?: ReactNode;

  /**
   * Status indicator
   */
  status?: 'online' | 'offline' | 'busy' | 'away';

  /**
   * Show status indicator
   * @default false
   */
  showStatus?: boolean;

  /**
   * Custom background color for text/icon avatars
   */
  bgColor?: string;

  /**
   * Custom text color for text/icon avatars
   */
  textColor?: string;
}

/**
 * Avatar component for user profile pictures
 *
 * @example
 * ```tsx
 * <Avatar src="/user.jpg" alt="John Doe" />
 * <Avatar fallback="JD" size="lg" />
 * <Avatar icon={<UserIcon />} />
 * <Avatar src="/user.jpg" status="online" showStatus />
 * ```
 */
export const Avatar: React.FC<AvatarProps> = ({
  size = 'base',
  shape = 'circle',
  src,
  alt = 'Avatar',
  fallback,
  icon,
  status,
  showStatus = false,
  bgColor,
  textColor,
  className = '',
  onError,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    if (onError) {
      onError(e);
    }
  };

  const classNames = [
    'avatar',
    `avatar-${size}`,
    `avatar-${shape}`,
    className
  ].filter(Boolean).join(' ');

  const hasImage = src && !imageError;
  const showFallback = !hasImage && (fallback || icon);

  const style: React.CSSProperties = {
    ...props.style,
  };

  if (showFallback && bgColor) {
    style.backgroundColor = bgColor;
  }

  if (showFallback && textColor) {
    style.color = textColor;
  }

  return (
    <div className={classNames} style={style}>
      {hasImage ? (
        <img
          src={src}
          alt={alt}
          className="avatar-image"
          onError={handleImageError}
          {...props}
        />
      ) : icon ? (
        <span className="avatar-icon">{icon}</span>
      ) : fallback ? (
        <span className="avatar-text">{fallback}</span>
      ) : (
        <span className="avatar-icon">👤</span>
      )}

      {showStatus && status && (
        <span className={`avatar-status avatar-status-${status}`} />
      )}
    </div>
  );
};

/**
 * Avatar Group component for displaying multiple avatars
 */
export interface AvatarGroupProps {
  /**
   * Size of all avatars in the group
   * @default 'base'
   */
  size?: AvatarProps['size'];

  /**
   * Maximum number of avatars to show
   * @default 3
   */
  max?: number;

  /**
   * Avatar components as children
   */
  children: ReactNode;

  /**
   * Additional class name
   */
  className?: string;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  size = 'base',
  max = 3,
  children,
  className = '',
}) => {
  const childArray = Array.isArray(children) ? children : [children];
  const visibleAvatars = childArray.slice(0, max);
  const remaining = childArray.length - max;

  const classNames = [
    'avatar-group',
    `avatar-group-${size}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames}>
      {visibleAvatars}
      {remaining > 0 && (
        <div className={`avatar avatar-${size} avatar-circle avatar-overflow`}>
          <span className="avatar-text">+{remaining}</span>
        </div>
      )}
    </div>
  );
};

export default Avatar;
